/**
 * Status-board answer buttons (2026-10-26, Andrej: "in CRHQ the status board
 * buttons add a message into the agent chat. Should be in Moniker too").
 *
 * Port of CRHQ's page SDK (control-room new/src/utils/artifact-bridge-core.js
 * + artifact-frame-core.js) so agents use ONE rule everywhere: in the board,
 *
 *   if (window.crhq) { const r = await crhq.prefill(text); if (r.ok) return; }
 *   navigator.clipboard.writeText(text);
 *
 * The board is written by an agent, so it is untrusted. It runs in a frame with
 * `allow-scripts` but WITHOUT `allow-same-origin`: it gets an opaque origin and
 * cannot reach the app, its cookies or its API. Through postMessage it may ask
 * for exactly one thing: add a line of text to the person's chat box. The
 * person always sees it and presses Send themselves; nothing is ever sent.
 *
 * A request counts only when it comes from the board frame itself, carries the
 * token of the document shown now, follows a click into the board (one answer
 * per click), passes cleanPrefillText, and stays under 20 a minute (0.4 s apart).
 */

export const BRIDGE_VERSION = 1;
export const BRIDGE_CAPS = Object.freeze(['chat.prefill']);
export const PREFILL_MAX_CHARS = 2000;
const MIN_INTERVAL_MS = 400;
const PER_MINUTE = 20;
const PAGE_TITLE_MAX = 80;

// Characters a person cannot see but an agent reads (controls except tab and
// newline, zero-width, bidi, tag characters, fillers, variation selectors).
const INVISIBLE_RE = new RegExp(
	'[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F-\\u009F\\u00AD\\u034F\\u061C\\u115F\\u1160\\u17B4\\u17B5' +
		'\\u180B-\\u180F\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u206F\\u3164\\uFEFF\\uFFA0\\uFFF9-\\uFFFB]' +
		'|\\uDB40[\\uDC00-\\uDC7F]|\\uDB40[\\uDD00-\\uDDEF]',
	'g',
);

/** The label line a sent message ends with (same text as CRHQ, so agents read it the same way). */
export const BUTTON_MARKER_RE = /\n*\[answered with a button on the page "([^"\n\]]{0,80})"\][ \t]*$/;
const ANY_MARKER_LINE_RE = /^[ \t]*\[answered with a button on the page "[^"\n]*"\][ \t]*$/gm;

export function cleanPageTitle(title: unknown): string {
	const t = String(title ?? '').replace(INVISIBLE_RE, '').replace(/["\]\[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim();
	return (t || "an agent's page").slice(0, PAGE_TITLE_MAX).trim();
}

export function cleanPrefillText(raw: unknown): { ok: true; text: string } | { ok: false; reason: string } {
	if (typeof raw !== 'string') return { ok: false, reason: 'not_text' };
	let t = raw.replace(/\r\n?/g, '\n').replace(/[\u2028\u2029]/g, '\n').replace(INVISIBLE_RE, '');
	t = t.replace(ANY_MARKER_LINE_RE, ''); // a page cannot write the label itself
	// Moniker addition: a button answer such as "2: Use a different airport — "
	// ends with ONE space so the person can type straight after it.
	const trailingSpace = /[^\s][ \t]+$/.test(t);
	t = t.replace(/[ \t]+$/gm, '').replace(/\n{5,}/g, '\n\n\n\n').trim();
	if (!t) return { ok: false, reason: 'empty' };
	if (trailingSpace) t += ' ';
	if (t.length > PREFILL_MAX_CHARS) return { ok: false, reason: 'too_long' };
	return { ok: true, text: t };
}

/** Add an answer as its own line under what the box already holds. */
export function appendAnswer(current: string, line: string): string {
	const cur = String(current ?? '').replace(/\s+$/, '');
	return cur ? `${cur}\n${line}` : line;
}

export function withButtonMarker(text: string, pageTitle: string): string {
	if (BUTTON_MARKER_RE.test(text)) return text;
	return `${text.replace(/\s+$/, '')}\n\n[answered with a button on the page "${cleanPageTitle(pageTitle)}"]`;
}

/** Split a message into its text and the page named by its label (null when none). */
export function splitButtonMarker(text: string): { text: string; page: string | null } {
	const m = BUTTON_MARKER_RE.exec(text);
	if (!m) return { text, page: null };
	return { text: text.slice(0, m.index).replace(/\s+$/, ''), page: m[1] };
}

/** Whether any answer added to the box is still in the text about to be sent. */
export function carriesAnswer(text: string, answers: string[]): boolean {
	return answers.some((a) => !!a && text.includes(a.trim()));
}

type BridgeMsg = { t: string; id: string; cap: string; args: Record<string, unknown> };

export function parseBridgeMessage(data: any): BridgeMsg | null {
	if (!data || typeof data !== 'object' || data.crhq !== 1) return null;
	const { t, id, cap } = data;
	if (typeof t !== 'string' || !/^[A-Za-z0-9]{16,64}$/.test(t)) return null;
	if (typeof id !== 'string' || !/^[A-Za-z0-9]{1,40}$/.test(id)) return null;
	if (typeof cap !== 'string' || cap.length > 40) return null;
	const args = data.args && typeof data.args === 'object' && !Array.isArray(data.args) ? data.args : {};
	return { t, id, cap, args };
}

export function newBridgeToken(): string {
	const a = new Uint8Array(18);
	crypto.getRandomValues(a);
	return Array.from(a, (b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36]).join('');
}

export function createRateLimit(now: () => number = () => Date.now()) {
	let times: number[] = [];
	return {
		take(): 'too_fast' | 'too_many' | null {
			const t = now();
			times = times.filter((x) => t - x < 60000);
			if (times.length && t - times[times.length - 1] < MIN_INTERVAL_MS) return 'too_fast';
			if (times.length >= PER_MINUTE) return 'too_many';
			times.push(t);
			return null;
		},
	};
}

/** window.crhq inside the board (same API as CRHQ's side panel). */
export function boardSdkScript(token: string): string {
	const t = JSON.stringify(String(token ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, 64));
	return '<script>(function(){if(window.crhq)return;var T=' + t + ',n=0,w={};' +
		'function post(c,a,ms){return new Promise(function(res){var id="q"+(++n)+Math.random().toString(36).slice(2,8);w[id]=res;' +
		'try{parent.postMessage({crhq:1,t:T,id:id,cap:c,args:a||{}},"*")}catch(e){delete w[id];res({ok:false,reason:"unavailable"});return}' +
		'setTimeout(function(){if(w[id]){delete w[id];res({ok:false,reason:"no_answer"})}},ms)})}' +
		'window.addEventListener("message",function(e){if(e.source!==parent)return;var d=e.data;if(!d||d.crhq!==1||typeof d.re!=="string"||!w[d.re])return;var r=w[d.re];delete w[d.re];r(d)});' +
		'var ready=(T&&window.parent&&window.parent!==window)?post("hello",{},1500).then(function(d){return{caps:d&&d.ok&&Array.isArray(d.caps)?d.caps.filter(function(c){return typeof c==="string"}):[]}}):Promise.resolve({caps:[]});' +
		'function out(d){return d&&d.ok?{ok:true}:{ok:false,reason:String(d&&d.reason||"unavailable")}}' +
		'window.crhq=Object.freeze({version:' + BRIDGE_VERSION + ',ready:ready,' +
		'prefill:function(text){return ready.then(function(r){if(r.caps.indexOf("chat.prefill")<0)return{ok:false,reason:"unavailable"};' +
		'return post("chat.prefill",{text:String(text==null?"":text)},4000).then(out)})}})' +
		'})();</script>';
}

/**
 * Storage stand-in: a frame without allow-same-origin throws on localStorage /
 * sessionStorage / document.cookie; boards that touch them would stop there.
 */
const STORAGE_STAND_IN =
	'<script>(function(){function mk(){var m={};return{getItem:function(k){return Object.prototype.hasOwnProperty.call(m,k)?m[k]:null},' +
	'setItem:function(k,v){m[k]=String(v)},removeItem:function(k){delete m[k]},clear:function(){m={}},' +
	'key:function(i){var k=Object.keys(m)[i];return k===undefined?null:k},get length(){return Object.keys(m).length}}}' +
	"['localStorage','sessionStorage'].forEach(function(n){try{window[n].length}catch(e){try{Object.defineProperty(window,n,{value:mk(),configurable:true})}catch(_){}}});" +
	"try{document.cookie}catch(e){try{var c='';Object.defineProperty(document,'cookie',{get:function(){return c},set:function(v){c=String(v).split(';')[0]},configurable:true})}catch(_){}}" +
	'})();</script>';

/** The board's HTML with the stand-in and SDK placed first (after <head>, <html> or the doctype). */
export function boardFrameDoc(html: string, token: string): string {
	const head = STORAGE_STAND_IN + boardSdkScript(token);
	for (const re of [/<head(?:\s[^>]*)?>/i, /<html(?:\s[^>]*)?>/i, /<!doctype[^>]*>/i]) {
		const m = re.exec(html);
		if (m) return html.slice(0, m.index + m[0].length) + head + html.slice(m.index + m[0].length);
	}
	return head + html;
}

/** The board page's <title>, for the label line. */
export function boardTitle(html: string): string {
	return cleanPageTitle(/<title[^>]*>([\s\S]*?)<\/title>/i.exec(html)?.[1] ?? 'status board');
}

export interface BoardBridgeOptions {
	getFrame(): HTMLIFrameElement | null;
	getToken(): string;
	check(): { ok: boolean; page?: string };
	onPrefill(text: string, page: string): boolean;
}

/** Listen for the board's requests. Returns detach(). */
export function attachBoardBridge(opts: BoardBridgeOptions, win: Window = window, doc: Document = document): () => void {
	let armed = false;
	let limit: ReturnType<typeof createRateLimit> | null = null;
	let limitToken = '';
	const frame = () => { try { return opts.getFrame(); } catch { return null; } };
	// "Armed" = the person clicked into the board since the last answer: focus
	// moved into the frame (the window blurs) and has not come back to the app.
	const arm = () => { const f = frame(); if (f && doc.activeElement === f) armed = true; };
	const onWindowBlur = () => { setTimeout(arm, 0); };
	const onWindowFocus = () => { armed = false; };
	const onFocusIn = (e: FocusEvent) => { const f = frame(); if (f && e.target === f) armed = true; };

	const onMessage = (e: MessageEvent) => {
		const f = frame();
		if (!f || !f.contentWindow || e.source !== f.contentWindow) return;
		if (e.origin !== 'null') return; // the frame never gets allow-same-origin
		const msg = parseBridgeMessage(e.data);
		const token = String(opts.getToken() || '');
		if (!msg || !token || msg.t !== token) return;
		const reply = (body: Record<string, unknown>) => {
			try { f.contentWindow?.postMessage({ crhq: 1, re: msg.id, ...body }, '*'); } catch { /* frame gone */ }
		};
		let ctx: { ok: boolean; page?: string };
		try { ctx = opts.check() || { ok: false }; } catch { ctx = { ok: false }; }
		if (!ctx.ok) { reply({ ok: false, reason: 'unavailable' }); return; }
		if (msg.cap === 'hello') { reply({ ok: true, v: BRIDGE_VERSION, caps: [...BRIDGE_CAPS] }); return; }
		if (msg.cap !== 'chat.prefill') { reply({ ok: false, reason: 'unknown' }); return; }
		const activation = (win.navigator as any)?.userActivation;
		if (!armed || (activation && !activation.isActive)) { reply({ ok: false, reason: 'click_first' }); return; }
		const cleaned = cleanPrefillText(msg.args.text);
		if (!cleaned.ok) { reply({ ok: false, reason: cleaned.reason }); return; }
		if (!limit || limitToken !== msg.t) { limit = createRateLimit(); limitToken = msg.t; }
		const limited = limit.take();
		if (limited) { reply({ ok: false, reason: limited }); return; }
		armed = false; // one answer per click into the board
		let taken = false;
		try { taken = opts.onPrefill(cleaned.text, cleanPageTitle(ctx.page)) !== false; } catch { taken = false; }
		if (!taken) { reply({ ok: false, reason: 'unavailable' }); return; }
		try { f.blur(); } catch { /* ignore */ }
		reply({ ok: true });
	};

	win.addEventListener('message', onMessage);
	win.addEventListener('blur', onWindowBlur);
	win.addEventListener('focus', onWindowFocus);
	doc.addEventListener('focusin', onFocusIn);
	return () => {
		win.removeEventListener('message', onMessage);
		win.removeEventListener('blur', onWindowBlur);
		win.removeEventListener('focus', onWindowFocus);
		doc.removeEventListener('focusin', onFocusIn);
	};
}
